// frontend/src/components/EmpresaPortal/CargaMasivaCandidatos.js
// Modal de carga masiva de candidatos vía Excel: descarga de plantilla,
// arrastrar y soltar, validación de CURP/correo, y corrección en línea
// antes de enviar — sin tener que reabrir el Excel.

import React, { useState, useRef } from 'react';
import * as XLSX from 'xlsx';
import {
  X, Download, FileSpreadsheet, CheckCircle2, AlertTriangle, Package,
  Trash2, Loader2, XCircle, PartyPopper, ClipboardList, ArrowLeft
} from 'lucide-react';

const CURP_REGEX = /^[A-Z][AEIOUX][A-Z]{2}\d{2}(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])[HM](AS|BC|BS|CC|CS|CH|CL|CM|DF|DG|GT|GR|HG|JC|MC|MN|MS|NT|NL|OC|PL|QO|QR|SP|SL|SR|TC|TS|TL|VZ|YN|ZS|NE)[B-DF-HJ-NP-TV-Z]{3}[A-Z0-9][0-9]$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

let idSecuencial = 0;
const siguienteId = () => `fila-${++idSecuencial}`;

const construirFila = (registro) => {
  const nombre = String(registro['Nombre'] ?? '').trim();
  const email = String(registro['Correo'] ?? '').trim();
  const curp = String(registro['CURP'] ?? '').trim().toUpperCase();

  return {
    _id: siguienteId(),
    nombre,
    email,
    curp,
    estadoEnvio: null, // null | 'enviando' | 'ok' | 'error'
    mensajeEnvio: null
  };
};

const validarFilas = (filas) => {
  const curpsVistas = new Map();
  filas.forEach(f => {
    if (f.curp) curpsVistas.set(f.curp, (curpsVistas.get(f.curp) || 0) + 1);
  });

  return filas.map(fila => {
    const errores = {};
    if (!fila.nombre) errores.nombre = 'El nombre es requerido';
    if (!fila.email || !EMAIL_REGEX.test(fila.email)) errores.email = 'Correo inválido';
    if (!CURP_REGEX.test(fila.curp)) errores.curp = 'CURP inválida (deben ser 18 caracteres con el formato oficial)';
    else if (curpsVistas.get(fila.curp) > 1) errores.curp = 'CURP repetida en este archivo';
    return { ...fila, errores };
  });
};

const CargaMasivaCandidatos = ({ info, onClose, onCompletado }) => {
  const [filas, setFilas] = useState([]);
  const [fase, setFase] = useState('upload'); // 'upload' | 'revision' | 'enviando' | 'resumen'
  const [arrastrando, setArrastrando] = useState(false);
  const [errorArchivo, setErrorArchivo] = useState(null);
  const inputRef = useRef(null);
  const [descargandoPlantilla, setDescargandoPlantilla] = useState(false);

  const stacksDisponibles = info?.stacks || [];
  const [stackSeleccionado, setStackSeleccionado] = useState(
    stacksDisponibles.length === 1 ? String(stacksDisponibles[0].id) : ''
  );

  const descargarPlantilla = async () => {
    setErrorArchivo(null);
    setDescargandoPlantilla(true);
    try {
      const response = await fetch('/api/empresas/me/plantilla', { credentials: 'include' });
      if (!response.ok) throw new Error('No se pudo descargar la plantilla');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const enlace = document.createElement('a');
      enlace.href = url;
      enlace.download = 'Plantilla_Candidatos.xlsx';
      document.body.appendChild(enlace);
      enlace.click();
      enlace.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setErrorArchivo(err.message);
    } finally {
      setDescargandoPlantilla(false);
    }
  };

  const procesarArchivo = (file) => {
    setErrorArchivo(null);

    if (!file) return;
    if (!stackSeleccionado) {
      setErrorArchivo('Primero elige el stack de pruebas que se usará para todos los candidatos de este archivo.');
      return;
    }
    const nombreValido = /\.(xlsx|xls)$/i.test(file.name);
    if (!nombreValido) {
      setErrorArchivo('Ese archivo no parece un Excel (.xlsx o .xls). Usa la plantilla descargada.');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const hoja = workbook.Sheets[workbook.SheetNames[0]];
        const registros = XLSX.utils.sheet_to_json(hoja, { defval: '' });

        const filasCrudas = registros
          .map(r => construirFila(r))
          .filter(f => f.nombre || f.email || f.curp);

        if (filasCrudas.length === 0) {
          setErrorArchivo('No se encontraron filas con datos en el archivo. Verifica que hayas llenado la tabla "Candidatos".');
          return;
        }

        setFilas(validarFilas(filasCrudas));
        setFase('revision');
      } catch (err) {
        console.error(err);
        setErrorArchivo('No se pudo leer el archivo. Asegúrate de usar la plantilla sin modificar su estructura de columnas.');
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setArrastrando(false);
    procesarArchivo(e.dataTransfer.files?.[0]);
  };

  const handleFileInput = (e) => {
    procesarArchivo(e.target.files?.[0]);
  };

  const actualizarFila = (id, campo, valor) => {
    setFilas(prev => validarFilas(
      prev.map(f => (f._id === id ? { ...f, [campo]: campo === 'curp' ? valor.toUpperCase() : valor } : f))
    ));
  };

  const eliminarFila = (id) => {
    setFilas(prev => validarFilas(prev.filter(f => f._id !== id)));
  };

  const filasConError = filas.filter(f => Object.keys(f.errores).length > 0);
  const filasListas = filas.filter(f => Object.keys(f.errores).length === 0);

  // Créditos: cada invitación reserva el costo del stack elegido
  const ilimitado = Boolean(info?.creditos?.ilimitado);
  const libres = info?.creditos?.libres ?? 0;
  const costoStack = stacksDisponibles.find(s => String(s.id) === String(stackSeleccionado))?.costo_creditos ?? 2;
  const cupoRestante = ilimitado ? null : Math.floor(libres / costoStack);
  const excedeCupo = cupoRestante !== null && filasListas.length > cupoRestante;

  const handleEnviar = async () => {
    if (filasConError.length > 0 || filasListas.length === 0) return;

    setFase('enviando');

    // Solo se procesan las filas que alcanza el saldo; al resto no se le
    // crea el candidato (así no queda registrado sin invitación)
    let enviables = cupoRestante === null ? Infinity : cupoRestante;
    for (const fila of filas) {
      if (Object.keys(fila.errores).length > 0) continue;
      if (enviables <= 0) {
        setFilas(prev => prev.map(f => (f._id === fila._id ? { ...f, estadoEnvio: 'error', mensajeEnvio: 'No enviado: créditos insuficientes' } : f)));
        continue;
      }
      enviables--;

      setFilas(prev => prev.map(f => (f._id === fila._id ? { ...f, estadoEnvio: 'enviando' } : f)));

      try {
        const candRes = await fetch('/api/candidatos', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ nombre_completo: fila.nombre, email: fila.email, curp: fila.curp })
        });
        const candData = await candRes.json();
        if (!candRes.ok) throw new Error(candData.error || 'No se pudo registrar el candidato');

        const asigRes = await fetch('/api/asignaciones', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ id_candidato: candData.id, id_stack: stackSeleccionado })
        });
        const asigData = await asigRes.json();
        if (!asigRes.ok) throw new Error(asigData.error || 'No se pudo generar la invitación');

        setFilas(prev => prev.map(f => (f._id === fila._id ? { ...f, estadoEnvio: 'ok' } : f)));
      } catch (err) {
        setFilas(prev => prev.map(f => (f._id === fila._id ? { ...f, estadoEnvio: 'error', mensajeEnvio: err.message } : f)));
      }
    }

    setFase('resumen');
  };

  const handleFinalizar = () => {
    onCompletado();
    onClose();
  };

  const exitosos = filas.filter(f => f.estadoEnvio === 'ok').length;
  const fallidos = filas.filter(f => f.estadoEnvio === 'error').length;

  return (
    <div className="ep-modal-overlay">
      <div className="ep-modal">
        <div className="ep-modal-header">
          <h2>Carga masiva de candidatos</h2>
          {fase !== 'enviando' && (
            <button className="ep-modal-close" onClick={onClose}><X size={18} /></button>
          )}
        </div>

        {fase === 'upload' && (
          <div className="ep-modal-body">
            <ol className="cm-pasos">
              <li>Elige el stack de pruebas que se asignará a todos los candidatos de este archivo.</li>
              <li>Descarga la plantilla de Excel.</li>
              <li>Llena las columnas Nombre, Correo y CURP (una fila por candidato).</li>
              <li>Arrastra el archivo lleno aquí abajo.</li>
            </ol>

            <div className="ep-field cm-field-stack">
              <label>Stack de pruebas para todo el archivo</label>
              <select value={stackSeleccionado} onChange={(e) => setStackSeleccionado(e.target.value)}>
                <option value="">Selecciona un stack...</option>
                {stacksDisponibles.map(s => (
                  <option key={s.id} value={s.id}>{s.nombre}</option>
                ))}
              </select>
            </div>

            <button
              type="button"
              className="cm-btn-plantilla"
              onClick={descargarPlantilla}
              disabled={descargandoPlantilla}
            >
              {descargandoPlantilla ? 'Descargando...' : <><Download size={15} /> Descargar plantilla de Excel</>}
            </button>

            <div
              className={`cm-dropzone ${arrastrando ? 'cm-dropzone-active' : ''} ${!stackSeleccionado ? 'cm-dropzone-disabled' : ''}`}
              onDragOver={(e) => { e.preventDefault(); if (stackSeleccionado) setArrastrando(true); }}
              onDragLeave={() => setArrastrando(false)}
              onDrop={handleDrop}
              onClick={() => stackSeleccionado && inputRef.current?.click()}
            >
              <div className="cm-dropzone-icon"><FileSpreadsheet size={40} /></div>
              <p>
                {stackSeleccionado
                  ? 'Arrastra aquí tu Excel lleno, o haz clic para elegirlo'
                  : 'Elige primero un stack arriba para poder subir el archivo'}
              </p>
              <small>Formatos aceptados: .xlsx, .xls</small>
              <input
                ref={inputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileInput}
                disabled={!stackSeleccionado}
                style={{ display: 'none' }}
              />
            </div>

            {errorArchivo && <div className="ep-feedback ep-feedback-error">{errorArchivo}</div>}
          </div>
        )}

        {(fase === 'revision' || fase === 'enviando') && (
          <div className="ep-modal-body">
            <div className="cm-resumen-bar">
              <span className="cm-resumen-item cm-resumen-ok"><CheckCircle2 size={13} /> {filasListas.length} listas</span>
              {filasConError.length > 0 && (
                <span className="cm-resumen-item cm-resumen-error"><AlertTriangle size={13} /> {filasConError.length} con errores</span>
              )}
              <span className="cm-resumen-item">{filas.length} en total</span>
              <span className="cm-resumen-item">
                <Package size={13} /> Stack: {stacksDisponibles.find(s => String(s.id) === String(stackSeleccionado))?.nombre || '—'}
              </span>
            </div>

            {excedeCupo && fase === 'revision' && (
              <div className="ep-warning">
                Tienes {filasListas.length} candidatos listos, pero con tus {libres} crédito{libres === 1 ? '' : 's'} libre{libres === 1 ? '' : 's'} alcanza
                para {cupoRestante} invitaci{cupoRestante === 1 ? 'ón' : 'ones'} de este stack ({costoStack} crédito{costoStack === 1 ? '' : 's'} c/u).
                Se enviarán las primeras {cupoRestante}; el resto no se procesará.
                {cupoRestante === 0 && (
                  <> Para adquirir más créditos, escríbenos a <a href="mailto:contacto@doublecoding.com">contacto@doublecoding.com</a>.</>
                )}
              </div>
            )}

            <div className="cm-tabla-wrap">
              <table className="cm-tabla">
                <thead>
                  <tr>
                    <th>Nombre</th>
                    <th>Correo</th>
                    <th>CURP</th>
                    <th></th>
                    {fase === 'revision' && <th></th>}
                  </tr>
                </thead>
                <tbody>
                  {filas.map(fila => (
                    <tr key={fila._id} className={Object.keys(fila.errores).length > 0 ? 'cm-fila-error' : ''}>
                      <td>
                        <input
                          type="text"
                          value={fila.nombre}
                          onChange={(e) => actualizarFila(fila._id, 'nombre', e.target.value)}
                          disabled={fase === 'enviando'}
                          className={fila.errores.nombre ? 'cm-input-error' : ''}
                        />
                        {fila.errores.nombre && <small className="cm-error-msg">{fila.errores.nombre}</small>}
                      </td>
                      <td>
                        <input
                          type="text"
                          value={fila.email}
                          onChange={(e) => actualizarFila(fila._id, 'email', e.target.value)}
                          disabled={fase === 'enviando'}
                          className={fila.errores.email ? 'cm-input-error' : ''}
                        />
                        {fila.errores.email && <small className="cm-error-msg">{fila.errores.email}</small>}
                      </td>
                      <td>
                        <input
                          type="text"
                          value={fila.curp}
                          maxLength={18}
                          onChange={(e) => actualizarFila(fila._id, 'curp', e.target.value)}
                          disabled={fase === 'enviando'}
                          className={fila.errores.curp ? 'cm-input-error' : ''}
                        />
                        {fila.errores.curp && <small className="cm-error-msg">{fila.errores.curp}</small>}
                      </td>
                      <td>
                        {fila.estadoEnvio === 'enviando' && <span className="cm-estado cm-estado-spin"><Loader2 size={15} /></span>}
                        {fila.estadoEnvio === 'ok' && <span className="cm-estado cm-estado-ok"><CheckCircle2 size={15} /></span>}
                        {fila.estadoEnvio === 'error' && (
                          <span className="cm-estado cm-estado-error" title={fila.mensajeEnvio}><XCircle size={15} /></span>
                        )}
                      </td>
                      {fase === 'revision' && (
                        <td>
                          <button type="button" className="cm-btn-quitar" onClick={() => eliminarFila(fila._id)} title="Quitar fila">
                            <Trash2 size={15} />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {filasConError.length > 0 && (
              <p className="cm-ayuda-error">
                Corrige los campos marcados en rojo directamente en la tabla, o quita esas filas, antes de enviar.
              </p>
            )}
          </div>
        )}

        {fase === 'resumen' && (
          <div className="ep-modal-body">
            <div className="cm-resumen-final">
              <div className="cm-resumen-final-icon">{fallidos === 0 ? <PartyPopper size={42} /> : <ClipboardList size={42} />}</div>
              <h3>{exitosos} de {exitosos + fallidos} candidatos invitados correctamente</h3>
              {fallidos > 0 && (
                <p className="cm-resumen-final-sub">
                  {fallidos} no se pudieron procesar. Revisa el detalle abajo — puedes corregirlos e intentarlo de nuevo con un archivo más chico.
                </p>
              )}
            </div>

            <div className="cm-tabla-wrap">
              <table className="cm-tabla">
                <thead>
                  <tr>
                    <th>Nombre</th>
                    <th>Correo</th>
                    <th>Resultado</th>
                  </tr>
                </thead>
                <tbody>
                  {filas.filter(f => f.estadoEnvio).map(fila => (
                    <tr key={fila._id}>
                      <td>{fila.nombre}</td>
                      <td>{fila.email}</td>
                      <td>
                        {fila.estadoEnvio === 'ok' ? (
                          <span className="cm-resultado-ok"><CheckCircle2 size={14} /> Invitado</span>
                        ) : (
                          <span className="cm-resultado-error" title={fila.mensajeEnvio}><XCircle size={14} /> {fila.mensajeEnvio}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="ep-modal-footer">
          {fase === 'upload' && (
            <button className="ep-btn-secondary" onClick={onClose}>Cancelar</button>
          )}
          {fase === 'revision' && (
            <>
              <button className="ep-btn-secondary" onClick={() => setFase('upload')}><ArrowLeft size={14} /> Elegir otro archivo</button>
              <button
                className="ep-btn-primary"
                onClick={handleEnviar}
                disabled={filasConError.length > 0 || filasListas.length === 0}
              >
                Enviar {filasListas.length} candidato{filasListas.length !== 1 ? 's' : ''}
              </button>
            </>
          )}
          {fase === 'enviando' && (
            <button className="ep-btn-primary" disabled>Enviando...</button>
          )}
          {fase === 'resumen' && (
            <button className="ep-btn-primary" onClick={handleFinalizar}>Listo</button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CargaMasivaCandidatos;
